import { ATLASSIAN_ACCOUNT_ID, type SnippetOwner } from '../snippetTypes.ts';

import { fetchUsersBatch } from './blockServiceAggUserQuery.ts';
import { USER_CACHE } from './userCache.ts';

const ATLASSIAN_SNIPPET_OWNER: SnippetOwner = {
	accountId: ATLASSIAN_ACCOUNT_ID,
	displayName: 'Atlassian',
};

export const getFallbackUserAvatarUrl = (userId: string): string => {
	return `/wiki/aa-avatar/${encodeURIComponent(userId)}`;
};

export const getDefaultUserInfo = (userId: string, displayName: string | null): SnippetOwner => {
	return {
		accountId: userId,
		displayName: displayName ?? userId,
		avatarUrl: getFallbackUserAvatarUrl(userId),
	};
};

/**
 * Resolves user info for the given account IDs using cache and a single batch API call for uncached IDs.
 * Returns a Map keyed by the requested accountId (original casing) for stable lookup.
 */
export const getCachedOrFetchUserInfos = async (
	accountIds: string[],
): Promise<Map<string, SnippetOwner>> => {
	const resultMap = new Map<string, SnippetOwner>();
	const uniqueAccountIds = [...new Set(accountIds.filter(Boolean))];
	const uncachedAccountIds: string[] = [];

	for (const id of uniqueAccountIds) {
		if (id.toLowerCase() === ATLASSIAN_ACCOUNT_ID.toLowerCase()) {
			resultMap.set(id, ATLASSIAN_SNIPPET_OWNER);
			continue;
		}

		const cachedSnippetOwner = USER_CACHE.get(id.toLowerCase());
		if (cachedSnippetOwner) {
			resultMap.set(id, cachedSnippetOwner);
			continue;
		}

		uncachedAccountIds.push(id);
	}

	if (uncachedAccountIds.length > 0) {
		const batchUserData = await fetchUsersBatch(uncachedAccountIds);
		for (const userData of batchUserData) {
			const owner: SnippetOwner = {
				accountId: userData.accountId,
				displayName: userData.displayName ?? userData.accountId,
				avatarUrl: userData.avatarUrl ?? getFallbackUserAvatarUrl(userData.accountId),
			};
			USER_CACHE.set(userData.accountId.toLowerCase(), owner);
		}

		for (const id of uncachedAccountIds) {
			const owner = USER_CACHE.get(id.toLowerCase()) ?? getDefaultUserInfo(id, null);
			resultMap.set(id, owner);
		}
	}

	return resultMap;
};
